/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { Button, Icon, Text } from '@az/starc-ui';
import { ArrowRight } from '@az/starc-ui-icons';
import type { ContentStackLiveTextHero } from '@/features/contentstack/interface';
import { useEffect, useState, type CSSProperties } from 'react';
import LegalText from './LegalText';
import { resolveColor } from './utils';
import styles from './HeroHeaderContent.module.scss';

type HeroHeaderSlide = ContentStackLiveTextHero['hero'][number];

export type HeroHeaderContentProps = {
  slides: HeroHeaderSlide[];
  currentSlide: number;
  previousSlide: number;
};

type StarcButtonVariant = 'primary' | 'secondary' | 'ghost' | 'pill';

const CS_TO_STARC_VARIANT: Record<string, StarcButtonVariant> = {
  Primary: 'primary',
  Secondary: 'secondary',
  Super: 'primary',
  Tertiary: 'secondary',
  Ghost: 'ghost',
  Pill: 'pill',
  Chevron: 'ghost',
};

const getButtonVariant = (theme?: string | null): StarcButtonVariant =>
  (theme ? CS_TO_STARC_VARIANT[theme] : undefined) ?? 'primary';

type HeroContentProps = {
  header?: string;
  subtitle?: string;
  buttonText?: string;
  buttonTheme?: string | null;
  link?: string;
  legalText?: string;
  legalCopy?: string;
  textColor?: string;
  isDarkBackground?: boolean;
  centerContentOnMobile?: boolean;
};

export const HeroContent = ({
  header,
  subtitle,
  buttonText,
  buttonTheme,
  link,
  legalText,
  legalCopy,
  textColor,
  isDarkBackground = true,
  centerContentOnMobile = false,
}: HeroContentProps) => {
  // 'as const' keeps the literal type so STARC's color union accepts it
  const starcColor = isDarkBackground ? 'on-primary' : 'primary';
  // Custom hex from CS: applied on the wrapper so it cascades when STARC color is omitted
  const colorOverrideStyle: CSSProperties | undefined = textColor
    ? ({ '--st-text-color': textColor } as CSSProperties)
    : undefined;

  if (!header && !subtitle && !buttonText && !legalText) {
    return null;
  }

  const textBlock = (
    <div className={styles.TextContent} style={colorOverrideStyle}>
      {header && (
        <Text
          as="h1"
          variant="display-2"
          color={textColor ? undefined : starcColor}
          className={styles.Header}
        >
          {header}
        </Text>
      )}
      {subtitle && (
        <Text
          as="div"
          variant="large-body"
          color={textColor ? undefined : starcColor}
          className={styles.Subtitle}
        >
          {subtitle}
          <Icon
            svg={ArrowRight}
            size={6}
            color={textColor ? undefined : starcColor}
            attributes={textColor ? { style: { color: textColor } } : undefined}
            className={styles.SubtitleIcon}
          />
        </Text>
      )}
    </div>
  );

  return (
    <div
      className={`${styles.Content} ${centerContentOnMobile ? styles.ContentMobileCentered : ''}`}
    >
      {(header || subtitle) &&
        (link ? (
          <>
            {/* Mobile: anchor is tabbable CTA since PrimaryButton is hidden */}
            <a href={link} className={`${styles.TextLink} ${styles.TextLinkMobile}`}>
              {textBlock}
            </a>
            {/* Desktop: plain div, Button handles navigation */}
            <div className={styles.TextLinkDesktop}>{textBlock}</div>
          </>
        ) : (
          textBlock
        ))}

      {buttonText && (
        <Button
          variant={getButtonVariant(buttonTheme)}
          href={link}
          className={styles.PrimaryButton}
        >
          {buttonText}
        </Button>
      )}

      {legalText && (
        <LegalText
          text={legalText}
          isDarkBackground={isDarkBackground}
          copy={legalCopy}
          className={styles.HeaderLegalText}
          textColor={textColor}
        />
      )}
    </div>
  );
};

const getLegalText = (slide: HeroHeaderSlide) => slide.legal?.text || slide.legal?.link_name;

const HeroHeaderContent = ({ slides, currentSlide, previousSlide }: HeroHeaderContentProps) => {
  const [trackPosition, setTrackPosition] = useState(currentSlide + 1);
  const [isResettingPosition, setIsResettingPosition] = useState(false);
  const slideCount = slides.length;

  useEffect(() => {
    if (slideCount < 2 || currentSlide === previousSlide) {
      return;
    }

    if (previousSlide === slideCount - 1 && currentSlide === 0) {
      setTrackPosition(slideCount + 1);
    } else if (previousSlide === 0 && currentSlide === slideCount - 1) {
      setTrackPosition(0);
    } else {
      setTrackPosition(currentSlide + 1);
    }
  }, [currentSlide, previousSlide, slideCount]);

  const handleTransitionEnd = () => {
    if (trackPosition !== 0 && trackPosition !== slideCount + 1) {
      return;
    }

    setIsResettingPosition(true);
    setTrackPosition(currentSlide + 1);
    requestAnimationFrame(() => setIsResettingPosition(false));
  };

  if (!slideCount) {
    return null;
  }

  const trackSlides = [slides[slideCount - 1], ...slides, slides[0]];

  return (
    <div className={styles.HeroHeaderContent}>
      <div
        className={`${styles.ContentTrack} ${isResettingPosition ? styles.ContentTrackReset : ''}`}
        style={{ transform: `translateX(-${trackPosition * 100}%)` }}
        onTransitionEnd={handleTransitionEnd}
      >
        {trackSlides.map((slide, index) => {
          return (
            <div
              key={`${slide.image?.image_url ?? slide.heading ?? 'live-text-hero'}-${index}`}
              className={styles.ContentSlide}
              aria-hidden={index !== trackPosition}
              inert={index !== trackPosition || undefined}
            >
              <HeroContent
                header={slide.heading}
                subtitle={slide.sub_heading}
                buttonText={slide.button?.title}
                buttonTheme={slide.button?.theme}
                link={slide.link}
                legalText={getLegalText(slide)}
                legalCopy={slide.legal?.copy}
                textColor={resolveColor(slide.text_color)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HeroHeaderContent;
